
import {
  Component,
  EventEmitter,
  HostListener,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import {
  debounceTime,
  distinctUntilChanged,
  merge,
  of,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { LandingService } from 'src/app/app-modules/public-layout/_services/landing.service';
import { AnalyticsService } from 'src/app/Shared/_services/analytics/analytics.service';
import { LocalizedNamePipe } from 'src/app/Shared/pipes/localized-name.pipe';

export interface SearchItem {
  id: string;
  name: string;
  imageUrl?: string;
  type?: string;
  auction_name?: string;
  hip_number?: string | null;
  slug?: string | null;
  aucSlug?: string | null;
}
@Component({
    selector: 'app-header-search-input',
        standalone: true,
imports: [FormsModule, MatIconModule, TranslatePipe, LocalizedNamePipe],
    templateUrl: './header-search-input.component.html',
    styleUrl: './header-search-input.component.scss'
})
export class HeaderSearchInputComponent {
  @Output() search = new EventEmitter<string>();

  searchTerm = '';
  isSearchOpen = false;
  searchResults: SearchItem[] = [];
  totalResults = 0;
  showResults = false;
  isLoading = false;
  private searchTerms = new Subject<string>(); // Debounced searches (typing)
  private immediateSearch = new Subject<string>(); // Immediate searches (paste, first search)
  private subscription = new Subscription();
  private currentPage = 1;
  private pageSize = 5; // Number of items to display in dropdown
  private isLoadingMore = false;
  private hasMoreResults = true;
  isFirstSearch = true; // Track if this is the first search

  constructor(
    private searchService: LandingService,
    private router: Router,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit(): void {
    // Debounced stream for typing
    const debouncedSearch$ = this.searchTerms.pipe(
      debounceTime(300),
      distinctUntilChanged()
    );

    // Merge debounced and immediate searches, use switchMap to cancel previous requests
    this.subscription.add(
      merge(debouncedSearch$, this.immediateSearch)
        .pipe(
          tap((term) => {
            if (term.trim().length > 2) {
              this.isLoading = true;
              this.searchResults = [];
            }
          }),
          switchMap((term) => {
            if (term.trim().length > 2) {
              return this.searchService.getSearchItems({
                search_term: term,
                page_size: this.pageSize,
                page: 1,
              });
            } else {
              this.searchResults = [];
              this.showResults = false;
              this.isFirstSearch = true;
              return of(null);
            }
          })
        )
        .subscribe({
          next: (response) => {
            if (response) {
              this.currentPage = 1;
              this.hasMoreResults = true;
              const newResults = this.formatSearchResults(response.data?.items || []);
              this.searchResults = newResults;
              this.totalResults = response?.data?.pagination?.total || 0;
              this.hasMoreResults = this.searchResults.length < this.totalResults;
              this.showResults = true;
            }
            this.isLoading = false;
          },
          error: (error) => {
            console.error('Search error:', error);
            this.searchResults = [];
            this.showResults = false;
            this.isLoading = false;
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.searchTerms.complete();
    this.immediateSearch.complete();
  }

  // Toggle search field (for tablet)
  toggleSearch() {
    this.isSearchOpen = !this.isSearchOpen;

    // When opening the search, focus the input and show results if we have any
    if (this.isSearchOpen && this.searchTerm !== ' ') {
      setTimeout(() => {
        const inputElement = document.querySelector('.search-input input');
        if (inputElement) {
          (inputElement as HTMLInputElement).focus();
          if (this.searchResults.length > 0) {
            this.showResults = true;
          }
        }
      }, 300); // Wait for animation to complete
    } else {
      this.showResults = false;
    }
  }

  // Handle click on search input (opens the dropdown)
  onSearchClick() {
    // Show the dropdown if we have results (don't trigger new fetch - let onInputChange handle it)
    if (this.searchResults.length > 0) {
      this.showResults = true;
    }
  }

  onInputChange(): void {
    // Reset first search flag when search term becomes empty
    if (this.searchTerm.trim().length === 0) {
      this.isFirstSearch = true;
      this.searchResults = [];
      this.showResults = false;
      return;
    }

    // For first search, trigger immediately (no debounce)
    if (this.isFirstSearch && this.searchTerm.trim().length > 2) {
      this.isFirstSearch = false;
      this.immediateSearch.next(this.searchTerm);
      return;
    }

    // For subsequent searches, use debounced approach
    this.searchTerms.next(this.searchTerm);
  }

  // Handle paste event - trigger immediate search
  onPaste(): void {
    // Use setTimeout to ensure pasted value is in the input
    setTimeout(() => {
      if (this.searchTerm.trim().length > 2) {
        this.isFirstSearch = false;
        this.immediateSearch.next(this.searchTerm);
      }
    }, 0);
  }

  onSearch() {
    if (this.searchTerm.trim()) {
      this.search.emit(this.searchTerm);

      // For manual search (button click), trigger immediately
      this.isFirstSearch = false;
      this.fetchSearchResults(this.searchTerm);
      this.showResults = true;
      // Track search
      // this.analyticsService.trackSearch(this.searchTerm, this.totalResults);
    }
  }

  selectItem(item: SearchItem, index: number = 0) {
    // Track search result selection
    // this.analyticsService.trackSelectSearchResult(this.searchTerm, item.id, index + 1);
    // Navigate to the item detail page
    this.router.navigate([
      '/buyer/auctions',
      item?.aucSlug,
      'horse',
      item?.slug,
    ]);
    this.showResults = false;
    this.searchTerm = '';
    this.searchResults = [];
    this.isFirstSearch = true;
  }

  private fetchSearchResults(term: string, loadMore = false): void {
    if (!loadMore) {
      // Reset pagination for new searches
      this.currentPage = 1;
      this.hasMoreResults = true;
    }

    if (!this.hasMoreResults && loadMore) {
      return; // Don't make API calls if we already know there are no more results
    }

    if (loadMore) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.searchResults = []; // Clear old results to prevent flash
    }

    // Call the search service
    this.searchService
      .getSearchItems({
        search_term: term,
        page_size: this.pageSize,
        page: this.currentPage,
      })
      .subscribe({
        next: (response) => {
          // Format the results for display
          const newResults = this.formatSearchResults(response.data?.items);
          if (loadMore) {
            // Append new results to existing ones
            this.searchResults = [...this.searchResults, ...newResults];
            this.isLoadingMore = false;
          } else {
            // Replace results for new searches
            this.searchResults = newResults;
            this.isLoading = false;
            // Track autocomplete results
            // this.analyticsService.trackSearchAutocomplete(term.length, newResults.length);
          }

          this.totalResults = response?.data?.pagination?.total;
          this.showResults = true;

          // Check if we have more results to load
          this.hasMoreResults = this.searchResults.length < this.totalResults;
        },
        error: (error) => {
          console.error('Search error:', error);
          if (loadMore) {
            this.isLoadingMore = false;
          } else {
            this.searchResults = [];
            this.showResults = false;
            this.isLoading = false;
          }
        },
      });
  }
  private formatSearchResults(data: any[]): SearchItem[] {
    // Transform the API data to SearchItem interface
    return data.map((item) => ({
      id: item.id,
      name: item.name,
      imageUrl:
        item.item_pictures[0]?.url || '/assets/images/defualt-image.jpg',
      type: item.type || 'item',
      hip_number: item.hip_number || null,
      auction_name: item.auction?.name || null,
      slug: item.slug || null,
      aucSlug: item.auction?.slug || null,
      status: item?.status,
    }));
  }
  onResultsScroll(event: Event): void {
    const element = event.target as HTMLElement;

    // Calculate if we're near the bottom (within 100px)
    const nearBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight < 100;

    // Load more if near bottom, not already loading, and have more results
    if (nearBottom && !this.isLoadingMore && this.hasMoreResults) {
      this.currentPage++;
      this.fetchSearchResults(this.searchTerm, true);
    }
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    // Check if click is outside the search component
    const clickTarget = event.target as HTMLElement;
    const searchComponents = [
      '.form-inner',
      '.form-inner2',
      '.search-results-dropdown',
      '.search-btn',
    ];

    // Check if click is inside any of the search components
    const isInsideSearch = searchComponents.some(
      (selector) => !!clickTarget.closest(selector)
    );

    if (!isInsideSearch) {
      this.showResults = false;
      this.searchResults = [];
    }
  }
}
